import { memo, useMemo, type CSSProperties } from 'react'
import {
  displayChord,
  parseChord,
  type Line,
  type Notation,
  type RevisionContent,
  type Section,
} from '../../domain'
import { t } from '../strings'
import { sectionTitle } from './sectionLabel'
import styles from './Sheet.module.css'
import { splitLine } from './splitLine'

/** Dónde está la marca: una aparición de una sección, una línea y un acorde. */
export interface SheetPosition {
  occurrenceId: string
  lineId: string
  chordId: string
}

export type PickHandler = (occurrenceId: string, lineId: string, chordId: string | null) => void

interface LineViewProps {
  occurrenceId: string
  line: Line
  sectionKind: Section['kind']
  notation: Notation
  /** Acorde marcado en esta línea, o null. */
  currentChordId: string | null
  onPick: PickHandler | undefined
}

/**
 * Una línea de la hoja. Va memorizada: al avanzar la marca solo se repintan las dos líneas que
 * cambian, no toda la canción (el iPad Air 2 lo agradece).
 */
const LineView = memo(function LineView({
  occurrenceId,
  line,
  sectionKind,
  notation,
  currentChordId,
  onPick,
}: LineViewProps) {
  const words = useMemo(() => splitLine(line), [line])

  if (line.kind === 'comment') return <p className={styles.comment}>{line.text}</p>
  if (sectionKind === 'tab') return <pre className={styles.tab}>{line.text}</pre>

  const hasChords = line.chords.length > 0
  const isCurrent = currentChordId !== null
  const className = [isCurrent ? styles.lineCurrent : styles.line, onPick ? styles.pickable : '']
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={className}
      data-line={`${occurrenceId}:${line.id}`}
      onClick={onPick ? () => onPick(occurrenceId, line.id, null) : undefined}
    >
      {words.map((word) => (
        <span key={word.key} className={styles.word}>
          {word.pieces.map((piece, index) => (
            <span key={index} className={styles.piece}>
              {hasChords && (
                <span
                  className={
                    piece.chord?.id === currentChordId ? styles.chordCurrent : styles.chord
                  }
                  onClick={
                    onPick && piece.chord
                      ? (event) => {
                          event.stopPropagation()
                          onPick(occurrenceId, line.id, piece.chord!.id)
                        }
                      : undefined
                  }
                >
                  {piece.chord ? (
                    <span
                      className={parseChord(piece.chord.symbol) ? undefined : styles.unrecognized}
                    >
                      {displayChord(piece.chord.symbol, notation, { unicode: true })}
                    </span>
                  ) : (
                    ' '
                  )}
                </span>
              )}
              <span className={styles.lyric}>{piece.text || ' '}</span>
            </span>
          ))}
        </span>
      ))}
    </div>
  )
})

interface SheetProps {
  content: RevisionContent
  notation: Notation
  current?: SheetPosition | null
  onPick?: PickHandler
  className?: string
  style?: CSSProperties
}

/** La canción tal como se toca: la secuencia de interpretación, con las repeticiones a la vista. */
export function Sheet({ content, notation, current, onPick, className, style }: SheetProps) {
  const sectionsById = useMemo(
    () => new Map(content.sections.map((section) => [section.id, section])),
    [content.sections],
  )

  if (content.sequence.length === 0) {
    return <p className={styles.empty}>{t.sheet.empty}</p>
  }

  return (
    <div className={[styles.sheet, className].filter(Boolean).join(' ')} style={style}>
      {content.sequence.map((occurrence) => {
        const section = sectionsById.get(occurrence.section)
        if (!section) return null
        const title = sectionTitle(section)
        return (
          <section key={occurrence.id} data-occurrence={occurrence.id}>
            {title && <h2 className={styles.sectionTitle}>{title}</h2>}
            {section.lines.map((line) => (
              <LineView
                key={line.id}
                occurrenceId={occurrence.id}
                line={line}
                sectionKind={section.kind}
                notation={notation}
                currentChordId={
                  current?.occurrenceId === occurrence.id && current.lineId === line.id
                    ? current.chordId
                    : null
                }
                onPick={onPick}
              />
            ))}
          </section>
        )
      })}
    </div>
  )
}
